"use client";

import type { EditorialCardStyle } from "@/cms/appearance";
import PropertyEditorialCard from "./PropertyEditorialCard";
import PropertyClassicCard from "./PropertyClassicCard";
import { getCasesAction } from "@/actions/search";
import { CaseData, FilterOption } from "@/cms/models";
import React, { useEffect, useState, useTransition } from "react";
import PropertyGalleryCard from "./PropertyGalleryCard";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/navigation";
import PropertyListCard from "./PropertyListCard";
import SortCaseControl from "./ui/SortCaseControl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGrid,
  faList,
  faRows,
  faMap,
  faRectangleList,
} from "@fortawesome/sharp-light-svg-icons";
import FilterButton from "./ui/FilterButton";
import SortingButton from "./ui/SortingButton";
import { ControlledEntranceSection as EntranceSection } from "@/components/motion/legacy-entrances";
import PropertyRowCard from "./PropertyRowCard";
import PropertyDuoCard from "./PropertyDuoCard";
import MapWrapper from "./ui/MapWrapper";
import TiltCard from "./ui/TiltCard";
import { DefaultTake } from "@/utils/helper";
import LoadingRippleButton from "./ui/LoadingRippleButton";

export interface SearchFormProps {
  cardStyle?: "editorial" | "classic";
  editorialCard?: EditorialCardStyle;
  toolbarStyle?: "editorial" | "classic";
  title?: string;
  fallbackImageUrl?: string;
  totalCount: number;
  cases: CaseData[];
  typeFilters: FilterOption[];
  pageFilter: any;
  listViews: string[];
  sortOptions: { label: string; value: string; selected: boolean }[];
  tilt: boolean;
  shadow: "none" | "little" | "lots";
  /** On a look-system website, where `shadow` may be own CSS. */
  lookSystem?: boolean;
  hideSortFunction?: boolean;
  hideResultCount?: boolean;
}

const PropertySearchForm: React.FC<SearchFormProps> = ({
  cardStyle,
  editorialCard,
  toolbarStyle,
  title,
  fallbackImageUrl,
  totalCount: totalCountIn,
  cases,
  typeFilters,
  pageFilter,
  sortOptions,
  listViews,
  tilt,
  shadow,
  lookSystem,
  hideSortFunction,
  hideResultCount,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialFilter = searchParams.get("filter") || null;
  const initialQuery = searchParams.get("q") || "";
  const initialTake = parseInt(
    searchParams.get("take") || DefaultTake.toString(),
  );

  const defaultSorting = sortOptions.find((x) => x.selected)?.value;

  const [query, setQuery] = useState<string>(initialQuery);
  const [totalCount, setTotalCount] = useState<number>(totalCountIn);
  const [results, setResults] = useState<CaseData[]>(cases);
  const [listView, setListViewValue] = useState<string | null>(
    listViews[0] ?? "grid",
  );
  const [animatedCount, setAnimatedCount] = useState<number>(0);
  const [currentTypeFilter, setCurrentTypeFilter] = useState<string | null>(
    initialFilter ?? "all",
  );

  const [sortKey, setSortKey] = useState<string | null>(
    defaultSorting ?? "date_desc",
  );
  const [isPending, startTransition] = useTransition();
  const [loadingFilter, setLoadingFilter] = useState<string | null>(null);

  const [take, setTake] = useState(initialTake);

  useEffect(() => {
    const value = localStorage.getItem("list_view_mode");
    if (value && listViews.includes(value)) {
      setListViewValue(value);
    }
  }, [listViews]);

  useEffect(() => {
    const newQuery = searchParams.get("q") || "";

    if (newQuery !== query) {
      setQuery(newQuery);
      startTransition(async () => {
        const request = {
          query: newQuery,
          typeFilter:
            toolbarStyle === "classic" ? searchParams.get("filter") || "" : "",
          sortKey: sortKey,
          take: take,
        };

        const getCasesResponse = await getCasesAction({
          ...request,
          ...pageFilter,
        });

        const timestamp = new Date().getTime();
        setTotalCount(getCasesResponse.totalCount ?? 0);
        setResults(
          getCasesResponse.items?.map((x) => ({
            ...x,
            timestamp: timestamp,
          })) ?? [],
        );
      });
    }
  }, [searchParams, pageFilter, query, sortKey, take, toolbarStyle]);

  const takeMore = async () => {
    startTransition(async () => {
      const newTake = take + DefaultTake;
      updateSearchParam("take", newTake.toString());
      setTake(newTake);
      const request = {
        query: query,
        typeFilter: toolbarStyle === "classic" ? currentTypeFilter || "" : "",
        sortKey: sortKey,
        skip: results.length,
        take: DefaultTake,
      };

      const getCasesResponse = await getCasesAction({
        ...request,
        ...pageFilter,
      });

      const timestamp = new Date().getTime();
      setTotalCount(getCasesResponse.totalCount ?? 0);
      setResults(
        results.concat(
          getCasesResponse.items?.map((x) => ({
            ...x,
            timestamp: timestamp,
          })) ?? [],
        ),
      );
    });
  };

  const filterByType = async (typeFilter: string) => {
    startTransition(async () => {
      if (typeFilter === "all" || typeFilter === currentTypeFilter) {
        typeFilter = "";
      }

      updateSearchParam("filter", typeFilter);
      setCurrentTypeFilter(typeFilter);
      const request = {
        query: query,
        typeFilter: typeFilter,
        sortKey: sortKey,
        take: take,
      };

      const getCasesResponse = await getCasesAction({
        ...request,
        ...pageFilter,
      });

      const timestamp = new Date().getTime();
      setTotalCount(getCasesResponse.totalCount ?? 0);
      setResults(
        getCasesResponse.items?.map((x) => ({
          ...x,
          timestamp: timestamp,
        })) ?? [],
      );
    });
  };

  const updateSearchParam = (key: string, value: string) => {
    const params = new URLSearchParams(window.location.search);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }

    router.replace(`${pathname}?${params.toString()}`, {
      scroll: false,
    });
  };

  const sortCases = async (sortKey: string) => {
    setSortKey(sortKey);

    startTransition(async () => {
      const request = {
        query: query,
        typeFilter: currentTypeFilter ?? "",
        sortKey: sortKey,
        take: take,
      };
      const getCasesResponse = await getCasesAction({
        ...request,
        ...pageFilter,
      });

      const timestamp = new Date().getTime();
      setTotalCount(getCasesResponse.totalCount ?? 0);
      setResults(
        getCasesResponse.items?.map((x) => ({
          ...x,
          timestamp: timestamp,
        })) ?? [],
      );
    });
  };

  const switchViews = async (view: string) => {
    setListViewValue(view);
    localStorage.setItem("list_view_mode", view);
  };

  return (
    <div
      className={`flex flex-col p-4 gap-4 ${toolbarStyle === "editorial" ? "re-case-search" : toolbarStyle === "classic" ? "re-classic-search" : ""}`}
    >
      {toolbarStyle === "classic" && (
        <div className="re-classic-search-heading">
          {title && <h1>{title}</h1>}
          <form action={pathname} role="search">
            <label className="sr-only" htmlFor={`case-search-${pathname}`}>
              Søg adresse eller by
            </label>
            <input
              id={`case-search-${pathname}`}
              name="q"
              defaultValue={query}
              placeholder="Søg adresse eller by"
            />
            {currentTypeFilter && currentTypeFilter !== "all" && (
              <input type="hidden" name="filter" value={currentTypeFilter} />
            )}
            <button type="submit" aria-label="Søg">
              ⌕
            </button>
          </form>
        </div>
      )}
      <div className="flex flex-col gap-2 lg:flex-row justify-between">
        <div className={`flex flex-wrap gap-2 ${query ? "invisible" : ""}`}>
          {typeFilters &&
            typeFilters.map((filter) => (
              <FilterButton
                loading={loadingFilter === filter.namespace && isPending}
                key={filter.namespace}
                label={filter.displayName}
                selected={
                  currentTypeFilter === filter.namespace ||
                  (currentTypeFilter === "" && filter.namespace === "all")
                }
                onClick={async () => {
                  setLoadingFilter(filter.namespace);
                  await filterByType(filter.namespace);
                }}
              />
            ))}
        </div>
        {!pageFilter.sold && (
          <div className="flex justify-end gap-2 items-end flex-wrap">
            {sortOptions?.length > 1 &&
              !hideSortFunction &&
              listView !== "map" && (
                <SortCaseControl
                  name="case-sort"
                  ariaLabel="Case sort"
                  items={sortOptions}
                  classes="col-span-2"
                  onSelected={(selectedItem) => sortCases(selectedItem.value)}
                ></SortCaseControl>
              )}
            {listViews.length > 1 && (
              <div className="flex justify-end gap-2 items-end">
                {listViews.map((x) => {
                  switch (x) {
                    case "list":
                      return (
                        <SortingButton
                          key={x}
                          label="Listevisning"
                          isSelected={listView === "list"}
                          onClick={() => switchViews("list")}
                        >
                          <FontAwesomeIcon
                            className="size-6 flex"
                            icon={faList}
                          />
                        </SortingButton>
                      );

                    case "grid":
                      return (
                        <SortingButton
                          key={x}
                          label="Gittervisning"
                          isSelected={listView === "grid"}
                          onClick={() => switchViews("grid")}
                        >
                          <FontAwesomeIcon
                            className="size-6 flex"
                            icon={faGrid}
                          />
                        </SortingButton>
                      );

                    case "row":
                      return (
                        <SortingButton
                          key={x}
                          label="Rækkevisning"
                          isSelected={listView === "row"}
                          onClick={() => switchViews("row")}
                        >
                          <FontAwesomeIcon
                            className="size-6 flex"
                            icon={faRows}
                          />
                        </SortingButton>
                      );

                    case "duo":
                      return (
                        <SortingButton
                          key={x}
                          label="Duovisning"
                          isSelected={listView === "duo"}
                          onClick={() => switchViews("duo")}
                        >
                          <FontAwesomeIcon
                            className="size-6 flex"
                            icon={faRectangleList}
                          />
                        </SortingButton>
                      );

                    case "map":
                      return (
                        <SortingButton
                          key={x}
                          label="Kortvisning"
                          isSelected={listView === "map"}
                          onClick={() => switchViews("map")}
                        >
                          <FontAwesomeIcon
                            className="size-6 flex"
                            icon={faMap}
                          />
                        </SortingButton>
                      );
                  }
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {query && (
        <div className="flex items-center">
          <div className="relative mr-4">
            <h1 className="block text-3xl">
              Søgeresultater for <span>&#39;{query}&#39;</span>
            </h1>
            <button
              aria-label="Clear search"
              className="absolute -top-3 -right-5 text-2xl"
              onClick={async () => {
                setQuery("");
                const request = {
                  query: "",
                  typeFilter: "",
                  sortKey: sortKey,
                  take: take,
                };
                const getCasesResponse = await getCasesAction({
                  ...request,
                  ...pageFilter,
                });
                setResults(getCasesResponse.items ?? []);
                router.push(pathname);
              }}
              title="Slet søgning"
            >
              &times;
            </button>
          </div>
        </div>
      )}
      {cardStyle === "classic" &&
        results.length > 0 &&
        (listView === "grid" || listView === "list") && (
          <div className="re-classic-property-grid">
            {results.map((property) => (
              <PropertyClassicCard
                key={property.id}
                property={property}
                fallbackImageUrl={fallbackImageUrl}
              />
            ))}
          </div>
        )}
      {cardStyle === "editorial" &&
        results.length > 0 &&
        (listView === "grid" || listView === "list") && (
          <div
            className={
              listView === "grid"
                ? "re-case-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
                : "flex flex-col gap-8"
            }
          >
            {results.map((property) => (
              <PropertyEditorialCard
                key={property.id}
                property={property}
                layout={listView === "list" ? "horizontal" : "grid"}
                options={editorialCard}
              />
            ))}
          </div>
        )}

      {results.length > 0 &&
        cardStyle !== "editorial" &&
        cardStyle !== "classic" &&
        listView == "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((property: any, index) => (
              <EntranceSection
                key={`${index}-${property.timestamp}`}
                initial="initial"
                whileInView="animate"
                viewport={{
                  once: true,
                  margin: "100px",
                }}
                onAnimationStart={() => setAnimatedCount(index)}
                custom={index}
                variants={{
                  initial: {
                    opacity: 0,
                    y: 100,
                  },
                  animate: (index: number) => ({
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.05 * (index - animatedCount),
                    },
                  }),
                }}
              >
                {tilt ? (
                  <TiltCard>
                    <PropertyGalleryCard
                      key={property.id}
                      property={property}
                      shadow={shadow}
                      lookSystem={lookSystem}
                    />
                  </TiltCard>
                ) : (
                  <PropertyGalleryCard
                    key={property.id}
                    property={property}
                    shadow={shadow}
                    lookSystem={lookSystem}
                  />
                )}
              </EntranceSection>
            ))}
          </div>
        )}

      {results.length > 0 && listView == "row" && (
        <div className="grid grid-cols-1 gap-4">
          {results.map((property: any, index) => (
            <EntranceSection
              key={`${index}-${property.timestamp}`}
              initial="initial"
              whileInView="animate"
              viewport={{
                once: true,
                margin: "100px",
              }}
              onAnimationStart={() => setAnimatedCount(index)}
              custom={index}
              variants={{
                initial: {
                  opacity: 0,
                  y: 100,
                },
                animate: (index: number) => ({
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.05 * (index - animatedCount),
                  },
                }),
              }}
            >
              {tilt ? (
                <TiltCard>
                  <PropertyRowCard
                    key={property.id}
                    property={property}
                    shadow={shadow}
                    lookSystem={lookSystem}
                  />
                </TiltCard>
              ) : (
                <PropertyRowCard
                  key={property.id}
                  property={property}
                  shadow={shadow}
                  lookSystem={lookSystem}
                />
              )}
            </EntranceSection>
          ))}
        </div>
      )}

      {results.length > 0 && listView == "duo" && (
        <div className="grid grid-cols-1 gap-4">
          {results.map((property: any, index) => (
            <EntranceSection
              key={`${index}-${property.timestamp}`}
              initial="initial"
              whileInView="animate"
              viewport={{
                once: true,
                margin: "100px",
              }}
              onAnimationStart={() => setAnimatedCount(index)}
              custom={index}
              variants={{
                initial: {
                  opacity: 0,
                  y: 100,
                },
                animate: (index: number) => ({
                  opacity: 1,
                  y: 0,
                  transition: {
                    delay: 0.05 * (index - animatedCount),
                  },
                }),
              }}
            >
              {tilt ? (
                <TiltCard>
                  <PropertyDuoCard
                    key={property.id}
                    property={property}
                    shadow={shadow}
                    lookSystem={lookSystem}
                  />
                </TiltCard>
              ) : (
                <PropertyDuoCard
                  key={property.id}
                  property={property}
                  shadow={shadow}
                  lookSystem={lookSystem}
                />
              )}
            </EntranceSection>
          ))}
        </div>
      )}

      {results.length > 0 &&
        cardStyle !== "editorial" &&
        cardStyle !== "classic" &&
        listView == "list" && (
          <section className="flex flex-col gap-4">
            {results.map((property: any, index) => (
              <EntranceSection
                key={`${index}-${property.timestamp}`}
                initial="initial"
                whileInView="animate"
                viewport={{
                  once: true,
                  margin: "100px",
                }}
                onAnimationStart={() => setAnimatedCount(index)}
                custom={index}
                variants={{
                  initial: {
                    opacity: 0,
                    y: 100,
                  },
                  animate: (index: number) => ({
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.05 * (index - animatedCount),
                    },
                  }),
                }}
              >
                {tilt ? (
                  <TiltCard>
                    <PropertyListCard key={property.id} property={property} />
                  </TiltCard>
                ) : (
                  <PropertyListCard key={property.id} property={property} />
                )}
              </EntranceSection>
            ))}
          </section>
        )}

      <style jsx global>{`
        [data-search] .maplibregl-popup-content {
          padding: 0;
        }
      `}</style>

      {results.length > 0 && listView == "map" && (
        <div className="flex justify-center">
          <div className="w-full h-[80vh]" data-search>
            <MapWrapper
              markers={results
                .filter((x) => !!x.latitude && !!x.longitude)
                .map((property) => {
                  return {
                    longitude: property.longitude!,
                    latitude: property.latitude!,
                    popover:
                      cardStyle === "editorial" ? (
                        <PropertyEditorialCard
                          property={property}
                          options={{
                            ...editorialCard,
                            actionVisibility: "always",
                          }}
                        />
                      ) : (
                        <PropertyGalleryCard
                          key={property.id}
                          property={property}
                          shadow={shadow}
                          lookSystem={lookSystem}
                        />
                      ),
                  };
                })}
            />
          </div>
        </div>
      )}

      {totalCount > results.length && (
        <div className="flex justify-center">
          <LoadingRippleButton
            loading={isPending}
            onClick={() => {
              takeMore();
            }}
          >
            Hent flere
          </LoadingRippleButton>
        </div>
      )}

      {(hideResultCount !== true || results.length === 0) &&
        (results.length == 0 ? (
          <div className="bg-white border p-4">
            Ingen ejendomme passede til dine søgekriterier.
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="text-sm text-gray-500">
              Viser {results.length} af {totalCount} ejendomme
            </div>
          </div>
        ))}
    </div>
  );
};

export default PropertySearchForm;
